import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Map, { IMapMarker } from '../Map';
import SearchBar from '../SearchBar';
import useMobileViewport from '../../hooks/useMobileViewport';
import comingSoonMessages, {
	ComingSoonMessages,
} from '../../lib/comingSoonMessages';

export interface ILocationLink extends ILink {
	color: 'primary' | 'secondary';
}

/**
 * @usePostData
 * @post_type location
 */
export interface ILocation {
	marker?: IMapMarker;
	logo?: IImage;
	title?: string;
	permalink?: string;
	address?: string;
	comingSoon?: ComingSoonMessages;
	phone?: string;
	linkDetails?: ILink;
	linkDirections?: ILink;
	openingText?: string;
	hours?: IStoreHours[];
	banner?: IImage;
	bannerLink?: ILink;
	buttons?: ILocationLink[];
}

export interface ILocationProps extends IBaseElementProps {
	mapOptions?: google.maps.MapOptions;
	locations?: ILocation[];
	apiURL?: string;
	listView?: boolean;
	/**
	 * @noUI
	 */
	defaultState?: any;
	/**
	 * @noUI
	 */
	onSearchBarSubmit?: (state: any) => void;
	/**
	 * @noUI
	 */
	hashAsState?: boolean;
	/**
	 * @noUI
	 */
	includeTypeDropdownOnSearch?: boolean;
	/**
	 * @noUI
	 */
	hideSearchBar?: boolean;
	/**
	 * @noUI
	 */
	hideViewToggle?: boolean;
	sectionTitle?: string;
}

export interface IStoreHours {
	storeName?: string;
	permalink?: string;
	storeHours?: string[];
	openStatus?: IOpenStatus;
	hoursNote?: string;
}

export interface IOpenStatus {
	minutes?: number;
	status?: string;
	message?: string;
}

/**
 * Location
 * @requiresHooks
 */
const Location: React.FC<ILocationProps> = ({
	className,
	classNames = [],
	mapOptions = {},
	locations = [],
	listView = false,
	defaultState,
	onSearchBarSubmit = (state: any) => {},
	hashAsState,
	includeTypeDropdownOnSearch = true,
	hideSearchBar = false,
	hideViewToggle = false,
	sectionTitle,

}) => {
	const [selectedLocation, setSelectedLocation] =
		useState<ILocation | null>(null);

	const [isListView, setIsListView] = useState(listView);

	const [globalClock, setGlobalClock] = useState(0);

	const isMobile = useMobileViewport();

	useEffect(() => {
		//60000 <- minute
		const id = setInterval(
			() => setGlobalClock((oldClock) => oldClock + 1),
			60000,
		);
		return () => {
			clearInterval(id);
		};
	}, []);

	// display the sentence
	const displayHours = (totalMinutes) => {
		let outputString = '';
		// determine how many minutes are left, depending on how long the browser has been open
		let minutesLeft = totalMinutes - globalClock;

		// pull the necessary portions of the time remaining
		const hours = Math.floor(minutesLeft / 60);
		const minutes = minutesLeft % 60;

		// construct the sentence
		if (hours > 0) {
			outputString += '<strong>' + hours + '</strong>' + ' hour';
			if (hours != 1) {
				outputString += 's';
			}
			if (minutes > 0) {
				outputString += ' and ';
			}
		}
		if (minutes > 0) {
			outputString += '<strong>' + minutes + '</strong>' + ' minute';
			if (minutes != 1) {
				outputString += 's';
			}
		}
		return outputString;
	};

	// The open/closed line under a service's hours. Without an openStatus there is no
	// status to report: the arithmetic below would be NaN, which fails the <= 0 test and
	// renders a dangling "Opens in" with no time after it.
	const renderHourStatus = (hour: IStoreHours) => {
		if (!hour.openStatus) {
			return null;
		}

		return (
			<>
				<div className="location__hour-status">
					{hour.openStatus?.minutes - globalClock <= 0 ? (
						hour.openStatus?.status ? (
							'Open Now'
						) : (
							'Closed Now'
						)
					) : (
						<>
							{hour.openStatus?.status ? (
								<span>Closes in </span>
							) : (
								<span>Opens in </span>
							)}
							<div
								dangerouslySetInnerHTML={{
									__html: displayHours(
										hour.openStatus?.minutes,
									),
								}}
							/>
						</>
					)}
				</div>
				{hour.openStatus?.message && (
					<div className="location__hour-message">
						{hour.openStatus?.message}
					</div>
				)}
			</>
		);
	};

	return (
		<>
			{sectionTitle && (
				<h2 className="location__title">{sectionTitle}</h2>
			)}
			<div className={className || cn('location', ...classNames)}>
				{!hideSearchBar && (
					<SearchBar
						onSubmit={onSearchBarSubmit}
						hashAsState={hashAsState}
						includeTypeDropdown={includeTypeDropdownOnSearch}
						defaultState={defaultState}
					/>
				)}

				{locations.length > 0 && !hideViewToggle && (
					<div className="location__switch-container">
						<button
							type="button"
							className={cn(
								'location__switch',
								'location__switch--map',
								!isListView && 'active',
							)}
							aria-label="Map View"
							onClick={() => {
								if (!isListView) return;
								setIsListView(!isListView);
							}}
						>
							Map
						</button>
						<button
							type="button"
							className={cn(
								'location__switch',
								'location__switch--list',
								isListView && 'active',
							)}
							aria-label="List View"
							onClick={() => {
								if (isListView) return;
								isListView
									? setIsListView(false)
									: setIsListView(true);
							}}
						>
							List
						</button>
					</div>
				)}
				{!locations.length ? (
					<div className="text-center p-5">
						No locations found for your search criteria. Please try
						again.
					</div>
				) : !isListView ? (
					<>
						<div className="location__map">
							<Map
								{...mapOptions}
								markers={locations.reduce(
									(acc, location) => [
										...acc,
										{
											...location.marker,
											onClick: () =>
												setSelectedLocation(location),
										},
									],
									[],
								)}
							/>

							{selectedLocation && (
								<>
									<div className="location__overlay" />
									<div className="location__details">
										<button
											className="location__btn-close"
											onClick={() =>
												setSelectedLocation(null)
											}
											aria-label="Close"
											type="button"
										>
											<i className="icon icon-close" />
										</button>

										<div className="row">
											<div className="col">
												{selectedLocation.logo && (
													<Image
														{...selectedLocation.logo}
														className="location__logo"
													/>
												)}

												{Array.isArray(
													selectedLocation.buttons,
												) && (
													<div className="location__buttons">
														{selectedLocation.buttons.map(
															(
																button,
																btnIndex,
															) => (
																<Button
																	key={
																		btnIndex
																	}
																	href={
																		button.href
																	}
																	label={
																		button.label
																	}
																	className={`location__button--${
																		button.color ||
																		'primary'
																	}`}
																/>
															),
														)}
													</div>
												)}
												{!isMobile &&
													selectedLocation.banner &&
													selectedLocation.bannerLink && (
														<a
															href={
																selectedLocation
																	.bannerLink
																	.href
															}
															title={
																selectedLocation
																	.bannerLink
																	.label
															}
														>
															<Image
																{...selectedLocation.banner}
																className="location__banner"
															/>
														</a>
													)}
											</div>
											<div className="col">
												{selectedLocation.title && (
													<h6 className="location__list-title">
														{selectedLocation.permalink ? (
															<a
																href={
																	selectedLocation.permalink
																}
															>
																{
																	selectedLocation.title
																}
															</a>
														) : (
															selectedLocation.title
														)}
													</h6>
												)}
												{comingSoonMessages(
													selectedLocation.comingSoon,
												).map((message, messageIndex) => (
													<p
														className="location__coming-soon"
														key={messageIndex}
													>
														{message}
													</p>
												))}
												{selectedLocation.address && (
													<address
														className="location__address"
														dangerouslySetInnerHTML={{
															__html: selectedLocation.address.replace(
																/\\n/g,
																'<br />',
															),
														}}
													/>
												)}
												{(selectedLocation.linkDetails ||
													selectedLocation.linkDirections) && (
													<div className="location__links">
														{selectedLocation.linkDetails && (
															<a
																href={
																	selectedLocation
																		.linkDetails
																		.href
																}
																className="location__link"
															>
																{
																	selectedLocation
																		.linkDetails
																		.label
																}
															</a>
														)}
														{selectedLocation.linkDirections && (
															<a
																href={
																	selectedLocation
																		.linkDirections
																		.href
																}
																className="location__link"
																target="_blank"
																rel="noreferrer"
															>
																{
																	selectedLocation
																		.linkDirections
																		.label
																}
															</a>
														)}
													</div>
												)}
												{selectedLocation.phone && (
													<a
														href={`tel:${selectedLocation.phone}`}
														className="location__phone"
													>
														{selectedLocation.phone}
													</a>
												)}
											</div>
											<div className="col">
												{Array.isArray(
													selectedLocation.hours,
												) && (
													<div className="location__list-hours">
														{selectedLocation.hours.map(
															(
																hour,
																hourIndex,
															) => (
																<div
																	key={
																		hourIndex
																	}
																>
																	<h6 className="location__list-title">
																		{hour.permalink ? (
																			<a
																				href={
																					hour.permalink
																				}
																			>
																				{
																					hour.storeName
																				}
																			</a>
																		) : (
																			hour.storeName
																		)}
																	</h6>
																	{Array.isArray(
																		hour.storeHours,
																	) && (
																		<>
																			<div>
																				{hour.storeHours.map(
																					(
																						storeHour,
																						storeHourIndex,
																					) => (
																						<div
																							key={
																								storeHourIndex
																							}
																						>
																							{
																								storeHour
																							}
																						</div>
																					),
																				)}
																			</div>
																			{renderHourStatus(hour)}
																			{hour.hoursNote && (
																				<p className="location-single__hours-note">
																					{
																						hour.hoursNote
																					}
																				</p>
																			)}
																		</>
																	)}
																</div>
															),
														)}
													</div>
												)}
											</div>
										</div>
										{isMobile &&
											selectedLocation.banner &&
											selectedLocation.bannerLink && (
												<a
													href={
														selectedLocation
															.bannerLink.href
													}
													title={
														selectedLocation
															.bannerLink.label
													}
												>
													<Image
														{...selectedLocation.banner}
														className="location__banner"
													/>
												</a>
											)}
									</div>
								</>
							)}
						</div>
					</>
				) : (
					<div className="location__list">
						<ul className="location__list-items">
							{locations.map((location, index) => (
								<li className="location__list-item" key={index}>
									<div className="location__list-index">
										{index + 1}
									</div>
									{location.title && (
										<h6 className="location__list-title">
											{location.permalink ? (
												<a href={location.permalink}>
													{location.title}
												</a>
											) : (
												location.title
											)}
										</h6>
									)}
									{comingSoonMessages(
										location.comingSoon,
									).map((message, messageIndex) => (
										<p
											className="location__list-coming-soon"
											key={messageIndex}
										>
											{message}
										</p>
									))}
									{location.address && (
										<address
											className="location__list-address"
											dangerouslySetInnerHTML={{
												__html: location.address.replace(
													/\\n/g,
													'<br />',
												),
											}}
										/>
									)}
									{location.phone && (
										<a
											href={`tel:${location.phone}`}
											className="location__list-phone"
										>
											{location.phone}
										</a>
									)}
									{Array.isArray(location.hours) && (
										<div className="location__list-hours">
											{location.hours.map(
												(hour, hourIndex) => (
													<div key={hourIndex}>
														<h6 className="location__list-title">
															{hour.permalink ? (
																<a href={hour.permalink}>
																	{hour.storeName}
																</a>
															) : (
																hour.storeName
															)}
														</h6>
														{Array.isArray(
															hour.storeHours,
														) && (
															<>
																<div>
																	{hour.storeHours.map(
																		(
																			storeHour,
																			storeHourIndex,
																		) => (
																			<div
																				key={
																					storeHourIndex
																				}
																			>
																				{
																					storeHour
																				}
																			</div>
																		),
																	)}
																</div>
																{renderHourStatus(hour)}
																{hour.hoursNote && (
																	<p className="location-single__hours-note">
																		{
																			hour.hoursNote
																		}
																	</p>
																)}
															</>
														)}
													</div>
												),
											)}
										</div>
									)}
									<div>
										{location.linkDetails?.label && (
											<Button
												{...location.linkDetails}
												className="location__list-button"
											/>
										)}
										{location.linkDirections?.label && (
											<Button
												{...location.linkDirections}
												className="location__list-button"
											/>
										)}
									</div>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</>
	);
};

export default Location;
